import { screenshareSelected } from 'features/screenshare/actions';
import {
  selectSelectedScreenshare,
  selectedScreenshareChanged,
  selectShowAllScreens,
  showAllScreensChanged,
} from 'features/screenshare/screenshareSlice';
import { SelectedScreenshare } from 'features/screenshare/types';
import { reconcileScreenshareVisibilitySaga } from 'features/screenshare/sagas/reconcileScreenshareVisibilitySaga';
import { subscribeToScreenshareSaga } from 'features/screenshare/sagas/subscribeToScreenshareSaga';
import { unsubscribeFromScreenshareSaga } from 'features/screenshare/sagas/unsubscribeFromScreenshareSaga';
import { initScreenshareWhiteboardDataSaga } from 'features/content-whiteboard/sagas/initScreenshareWhiteboardDataSaga';
import { UserId } from 'features/users/types';
import { selectLocalUserId } from 'features/users/usersSlice';
import { call, put, select } from 'redux-saga/effects';
import { ContentData, GridPanelName } from 'features/layout/features/content/types';
import {
  selectGridPanel,
  selectLockedContent,
} from 'features/layout/features/content/contentSlice';
import { RTCClient } from 'utils/webrtc';

export function* onScreenshareSelectedSaga(action: ReturnType<typeof screenshareSelected>) {
  const {
    userId,
    feedId,
    initial,
    reinit,
    whiteboardShapes = [],
    existingScreenshare,
  } = action.payload;

  const localUserId: UserId = yield select(selectLocalUserId);

  const currentGridPanel: GridPanelName = yield select(selectGridPanel);

  const previousScreenshare: SelectedScreenshare | null = yield select(selectSelectedScreenshare);
  const showAllScreens: boolean = yield select(selectShowAllScreens);

  const lockedContent: ContentData = yield select(selectLockedContent);
  const runOnBackground =
    initial && lockedContent && lockedContent.id !== feedId && userId !== localUserId;

  const availableStreams = RTCClient.receivingFeed.availableScreenshares[feedId];
  const hasVideoStream = availableStreams?.some((item) => item.type === 'video');

  if (userId === previousScreenshare?.userId && previousScreenshare?.feedId === feedId) {
    if (currentGridPanel === 'screenshare' && (hasVideoStream || !initial) && !reinit) {
      yield call(initScreenshareWhiteboardDataSaga, {
        userId,
        feedId,
        shapes: userId === previousScreenshare?.userId ? whiteboardShapes : [],
      });
    }

    return;
  }

  if (previousScreenshare && !reinit && previousScreenshare.userId !== localUserId) {
    yield call(unsubscribeFromScreenshareSaga, previousScreenshare);
  }

  if (localUserId !== userId) {
    const alreadySubscribed = RTCClient.receivingFeed.subscribedScreenshares[feedId];
    if (!(reinit && alreadySubscribed)) {
      yield call(subscribeToScreenshareSaga, userId, feedId, availableStreams, {
        videoOnly: false,
        forceSubscribe: !initial,
      });
    }
  }

  if ((hasVideoStream || !initial) && !reinit && !runOnBackground) {
    yield put(selectedScreenshareChanged({ userId, feedId }));

    if (!(initial && existingScreenshare) || currentGridPanel === 'screenshare') {
      yield call(initScreenshareWhiteboardDataSaga, {
        userId,
        feedId,
        shapes: whiteboardShapes,
      });
    }
  }

  if (showAllScreens) {
    yield put(showAllScreensChanged(false));
  }

  yield call(RTCClient.receivingFeed.updateScreenshareSimulcastConfig);

  if (reinit || runOnBackground) {
    yield call(reconcileScreenshareVisibilitySaga);
  }
}
